/**
 * SAP backend axis — the ONE place that answers "is this build wired to a live
 * SAP S/4HANA backend?" (`VITE_TS_BACKEND=cats`, the S/4-hosted "Option A"
 * build) versus the default Supabase/demo backend.
 *
 * Mirrors `config/productSurface.ts` (the `VITE_SURFACE` axis). Before this
 * module, the identical `import.meta.env.VITE_TS_BACKEND === 'cats'` check was
 * copy-pasted across ~8 files as `IS_CATS` / `IS_CATS_BACKEND` /
 * `isSapHostedBuild` / `isCats`. Those should all import `IS_LIVE_SAP` from here
 * so the definition can't drift.
 *
 * This axis also encodes the charge-code SOURCE decision (the "which shelf"
 * call): in a live-SAP fork the timesheet reads its charge objects LIVE from
 * SAP (`sap_charge_objects`, filled + reconciled by the SAP pull — "Shelf A");
 * in the demo it reads the seeded `wbs_elements` + `charge_lines` /
 * `work_authorizations` ("Shelves B + C"). See `chargeCodeSource()`.
 *
 * Intentionally separate from the other deployment knobs:
 *   - `VITE_DATA_SOURCE` (mock | api | supabase) — where general PPM data comes from
 *   - `VITE_SURFACE` (internal | product)        — is the dev cockpit visible
 *   - `appMode` (demo | sandbox | dev | quality | production) — runtime data/auth posture
 */

export type SapBackend = 'live-sap' | 'demo'

/**
 * Resolved at build time from `VITE_TS_BACKEND`. `'cats'` → the S/4-hosted build
 * that talks to a live SAP backend same-origin. Unset → the default
 * Supabase/demo backend, so every existing deployment behaves exactly as before.
 */
export const IS_LIVE_SAP: boolean =
  (import.meta.env as Record<string, string | undefined>).VITE_TS_BACKEND === 'cats'

/** The SAP backend this build is wired to. */
export function sapBackend(): SapBackend {
  return IS_LIVE_SAP ? 'live-sap' : 'demo'
}

/** True on the S/4-hosted build wired to a live SAP backend (`VITE_TS_BACKEND=cats`). */
export function isLiveSapBackend(): boolean {
  return IS_LIVE_SAP
}

export type ChargeCodeSource = 'sap-charge-objects' | 'demo-tables'

/**
 * Where the timesheet sources its selectable charge codes — the encoded
 * "which shelf" decision:
 *   - `'sap-charge-objects'` — live-SAP fork: read from `sap_charge_objects`
 *     (Shelf A), filled + reconciled by the SAP pull.
 *   - `'demo-tables'`        — demo/default: read from the seeded `wbs_elements`
 *     + `charge_lines` / `work_authorizations` (Shelves B + C).
 */
export function chargeCodeSource(): ChargeCodeSource {
  return IS_LIVE_SAP ? 'sap-charge-objects' : 'demo-tables'
}

/**
 * The switch settings per deployment kind — the written-down "what do we set for
 * a real customer?" answer. Reference for fork setup and the flag-profile
 * decision; NOT read at runtime (each build sets its own env). `appMode` is the
 * runtime posture, listed here for completeness.
 */
export interface DeploymentProfile {
  readonly label: string
  readonly VITE_DATA_SOURCE: 'mock' | 'api' | 'supabase'
  readonly VITE_TS_BACKEND: 'cats' | 'unset'
  readonly VITE_SURFACE: 'internal' | 'product'
  readonly appMode: 'demo' | 'production'
  readonly chargeCodeSource: ChargeCodeSource
  readonly note: string
}

export const DEPLOYMENT_PROFILES = {
  /** The deployed demo (unchanged): Supabase data, no live SAP, dev cockpit on. */
  demo: {
    label: 'Demo (project-moonshot)',
    VITE_DATA_SOURCE: 'supabase',
    VITE_TS_BACKEND: 'unset',
    VITE_SURFACE: 'internal',
    appMode: 'demo',
    chargeCodeSource: 'demo-tables',
    note: 'Charge codes come from the seeded wbs_elements + charge_lines. Shelf A (sap_charge_objects) is empty until a pull runs; no live SAP.',
  },
  /** A real-customer clone (e.g. Codan) wired to their SAP: live SAP, clean product. */
  productionFork: {
    label: 'Customer production fork (e.g. Codan)',
    VITE_DATA_SOURCE: 'supabase',
    VITE_TS_BACKEND: 'cats',
    VITE_SURFACE: 'product',
    appMode: 'production',
    chargeCodeSource: 'sap-charge-objects',
    note: "Timesheet reads charge codes LIVE from SAP (Shelf A), fed by Josh's pull. Dev cockpit hidden; real auth.",
  },
} satisfies Record<string, DeploymentProfile>
